import { useState, useEffect, useCallback, useRef } from "react";
import { uGet as aGet, uPost as aPost, uPut as aPut } from "../../utils/userApi";
import { C, Input, Select, Btn, Modal } from "./AdminPanel";
import {
    FiSearch, FiEdit2, FiUserPlus, FiShield,
    FiUserX, FiRefreshCw, FiKey, FiUser,
    FiMail, FiPhone, FiLock, FiCheck, FiX
} from "react-icons/fi";
import { FaCrown, FaUserShield, FaUserTie } from "react-icons/fa";

const ROLE_HIERARCHY = { user: 0, employee: 1, admin: 2, superadmin: 3 };

const ROLE_META = {
    superadmin: { label: "Super Admin", icon: <FaUserShield />, color: "#f59e0b", bg: "#f59e0b18" },
    admin: { label: "Admin", icon: <FaCrown />, color: "#6366f1", bg: "#6366f118" },
    employee: { label: "Employee", icon: <FaUserTie />, color: "#10b981", bg: "#10b98118" },
};

function RoleBadge({ role }) {
    const m = ROLE_META[role];
    if (!m) return null;
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "3px 10px", borderRadius: 20,
            background: m.bg, color: m.color,
            fontSize: 11, fontWeight: 700,
            border: `1px solid ${m.color}30`,
        }}>
            {m.icon} {m.label}
        </span>
    );
}

export default function Staff({ toast, user: currentUser }) {
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [editStaff, setEditStaff] = useState(null);  // for role change
    const [resetPwd, setResetPwd] = useState(null);  // for password reset
    const debounceRef = useRef();

    const load = useCallback(() => {
        setLoading(true);
        aGet("/users/staff/list", { search })
            .then(d => setStaff(d.staff))
            .catch(e => toast(e.message, "error"))
            .finally(() => setLoading(false));
    }, [search, toast]);

    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(load, 350);
    }, [load]);

    const handleRevoke = async (member) => {
        if (!window.confirm(`Revoke staff access for ${member.name}? They will become a regular user.`)) return;
        try {
            await aPut(`/users/staff/${member._id}/revoke`);
            toast(`Access revoked for ${member.name}`);
            load();
        } catch (e) { toast(e.message, "error"); }
    };

    // What roles can the current user assign?
    const assignableRoles = Object.keys(ROLE_META).filter(
        r => ROLE_HIERARCHY[r] <= ROLE_HIERARCHY[currentUser?.role]
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

            {/* Header row */}
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
                    <FiSearch style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: C.muted, fontSize: 14 }} />
                    <input
                        placeholder="Search staff by name or email…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{
                            width: "100%", padding: "9px 12px 9px 34px",
                            border: `1px solid ${C.border}`, borderRadius: 8,
                            background: C.surface, color: C.text, fontSize: 13,
                            outline: "none", boxSizing: "border-box",
                        }}
                    />
                </div>
                <Btn onClick={() => setShowCreate(true)}>
                    <FiUserPlus /> Add Staff
                </Btn>
            </div>

            {/* Stats row */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {Object.entries(ROLE_META).map(([role, meta]) => {
                    const count = staff.filter(s => s.role === role).length;
                    return (
                        <div key={role} style={{
                            flex: 1, minWidth: 120,
                            background: meta.bg,
                            border: `1px solid ${meta.color}25`,
                            borderRadius: 12, padding: "14px 18px",
                            display: "flex", alignItems: "center", gap: 10,
                        }}>
                            <span style={{ fontSize: 22, color: meta.color }}>{meta.icon}</span>
                            <div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: meta.color }}>{count}</div>
                                <div style={{ fontSize: 11, color: meta.color + "cc", fontWeight: 600 }}>{meta.label}s</div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Staff cards */}
            {loading ? (
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
                    {[...Array(4)].map((_, i) => (
                        <div key={i} style={{ height: 110, borderRadius: 12, background: "#f1f2f6", animation: "pulse 1.5s infinite" }} />
                    ))}
                </div>
            ) : staff.length === 0 ? (
                <div style={{ textAlign: "center", padding: 60, color: C.muted, fontSize: 14 }}>
                    <FiUserPlus style={{ fontSize: 36, marginBottom: 12, display: "block", margin: "0 auto 12px" }} />
                    No staff found. Add your first staff member.
                </div>
            ) : (
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
                    {staff.map(member => (
                        <StaffCard
                            key={member._id}
                            member={member}
                            currentUser={currentUser}
                            assignableRoles={assignableRoles}
                            onRevoke={() => handleRevoke(member)}
                            onChangeRole={() => setEditStaff({ ...member })}
                            onResetPwd={() => setResetPwd({ ...member, newPassword: "" })}
                        />
                    ))}
                </div>
            )}

            {/* ── Create Staff Modal ── */}
            {showCreate && (
                <CreateStaffModal
                    toast={toast}
                    currentUser={currentUser}
                    assignableRoles={assignableRoles}
                    onClose={() => setShowCreate(false)}
                    onCreated={() => { setShowCreate(false); load(); }}
                />
            )}

            {/* ── Change Role Modal ── */}
            {editStaff && (
                <ChangeRoleModal
                    member={editStaff}
                    assignableRoles={assignableRoles}
                    toast={toast}
                    onClose={() => setEditStaff(null)}
                    onSaved={() => { setEditStaff(null); load(); }}
                />
            )}

            {/* ── Reset Password Modal ── */}
            {resetPwd && (
                <ResetPasswordModal
                    member={resetPwd}
                    toast={toast}
                    onClose={() => setResetPwd(null)}
                    onSaved={() => { setResetPwd(null); load(); }}
                />
            )}
        </div>
    );
}

// ── Staff Card ─────────────────────────────────────────────
function StaffCard({ member, currentUser, onRevoke, onChangeRole, onResetPwd }) {
    const canModify = ROLE_HIERARCHY[currentUser?.role] > ROLE_HIERARCHY[member.role];
    const isSelf = member._id === currentUser?._id;

    return (
        <div style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 14,
            padding: 18,
            display: "flex",
            flexDirection: "column",
            gap: 14,
            transition: "box-shadow 0.2s",
        }}>
            {/* Top: avatar + name + role */}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                    width: 44, height: 44, borderRadius: "50%",
                    background: (ROLE_META[member.role]?.color ?? C.muted) + "22",
                    color: ROLE_META[member.role]?.color ?? C.muted,
                    fontWeight: 800, fontSize: 18,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                }}>
                    {member.name?.charAt(0)?.toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.text, display: "flex", alignItems: "center", gap: 6 }}>
                        {member.name}
                        {isSelf && (
                            <span style={{ fontSize: 10, background: C.primary + "18", color: C.primary, padding: "1px 7px", borderRadius: 20, fontWeight: 600 }}>
                                You
                            </span>
                        )}
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {member.email}
                    </div>
                </div>
                <RoleBadge role={member.role} />
            </div>

            {/* Phone */}
            {member.phone && (
                <div style={{ fontSize: 12, color: C.muted, display: "flex", alignItems: "center", gap: 6 }}>
                    <FiPhone /> {member.phone}
                </div>
            )}

            {/* Joined */}
            <div style={{ fontSize: 11, color: C.muted }}>
                Joined {new Date(member.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
            </div>

            {/* Actions */}
            {!isSelf && canModify && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
                    <Btn size="sm" variant="ghost" onClick={onChangeRole}>
                        <FiShield /> Role
                    </Btn>
                    <Btn size="sm" variant="ghost" onClick={onResetPwd}>
                        <FiKey /> Password
                    </Btn>
                    <Btn size="sm" variant="red" onClick={onRevoke} style={{ marginLeft: "auto" }}>
                        <FiUserX /> Revoke
                    </Btn>
                </div>
            )}
        </div>
    );
}

// ── Create Staff Modal ─────────────────────────────────────
function CreateStaffModal({ toast, assignableRoles, onClose, onCreated }) {
    const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "employee" });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const handleCreate = async () => {
        if (!form.name || !form.email || !form.password) {
            toast("Name, email and password are required.", "error"); return;
        }
        setSaving(true);
        try {
            await aPost("/users/staff/create", form);
            toast(`${form.name} added as ${form.role} ✓`);
            onCreated();
        } catch (e) { toast(e.message, "error"); }
        finally { setSaving(false); }
    };

    return (
        <Modal title="Add Staff Member" onClose={onClose}>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                        <label style={labelStyle}>Full Name *</label>
                        <div style={iconInputWrap}>
                            <FiUser style={iconStyle} />
                            <input style={inputStyle} placeholder="John Doe" value={form.name} onChange={e => set("name", e.target.value)} />
                        </div>
                    </div>
                    <div>
                        <label style={labelStyle}>Phone</label>
                        <div style={iconInputWrap}>
                            <FiPhone style={iconStyle} />
                            <input style={inputStyle} placeholder="9876543210" value={form.phone} onChange={e => set("phone", e.target.value)} />
                        </div>
                    </div>
                </div>
                <div>
                    <label style={labelStyle}>Email Address *</label>
                    <div style={iconInputWrap}>
                        <FiMail style={iconStyle} />
                        <input style={inputStyle} type="email" placeholder="staff@kkb.com" value={form.email} onChange={e => set("email", e.target.value)} />
                    </div>
                </div>
                <div>
                    <label style={labelStyle}>Password *</label>
                    <div style={iconInputWrap}>
                        <FiLock style={iconStyle} />
                        <input style={inputStyle} type="password" placeholder="Min 6 characters" value={form.password} onChange={e => set("password", e.target.value)} />
                    </div>
                </div>
                <div>
                    <label style={labelStyle}>Role *</label>
                    <select
                        value={form.role}
                        onChange={e => set("role", e.target.value)}
                        style={{ ...inputStyle, paddingLeft: 12 }}
                    >
                        {assignableRoles.map(r => (
                            <option key={r} value={r}>{ROLE_META[r]?.label ?? r}</option>
                        ))}
                    </select>
                </div>

                {/* Role description */}
                <div style={{
                    background: (ROLE_META[form.role]?.bg ?? "#f5f6fa"),
                    border: `1px solid ${(ROLE_META[form.role]?.color ?? C.border)}30`,
                    borderRadius: 8, padding: "10px 14px", fontSize: 12,
                    color: ROLE_META[form.role]?.color ?? C.muted,
                }}>
                    {form.role === "employee" && "Employee: Can view orders, messages and dashboard only."}
                    {form.role === "admin" && "Admin: Full access except user management and staff creation of higher roles."}
                    {form.role === "superadmin" && "Super Admin: Full access to everything including user and staff management."}
                </div>

                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 4 }}>
                    <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
                    <Btn onClick={handleCreate} disabled={saving}>
                        {saving ? "Creating…" : <><FiUserPlus /> Create Staff</>}
                    </Btn>
                </div>
            </div>
        </Modal>
    );
}

// ── Change Role Modal ──────────────────────────────────────
function ChangeRoleModal({ member, assignableRoles, toast, onClose, onSaved }) {
    const [role, setRole] = useState(member.role);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            await aPut(`/users/staff/${member._id}/role`, { role });
            toast(`Role updated to ${ROLE_META[role]?.label ?? role} ✓`);
            onSaved();
        } catch (e) { toast(e.message, "error"); }
        finally { setSaving(false); }
    };

    return (
        <Modal title={`Change Role — ${member.name}`} onClose={onClose}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: C.bg, borderRadius: 10 }}>
                    <div style={{ width: 40, height: 40, borderRadius: "50%", background: ROLE_META[member.role]?.bg, color: ROLE_META[member.role]?.color, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>
                        {member.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{member.name}</div>
                        <div style={{ fontSize: 12, color: C.muted }}>{member.email}</div>
                    </div>
                    <div style={{ marginLeft: "auto" }}>
                        <RoleBadge role={member.role} />
                    </div>
                </div>

                <div>
                    <label style={labelStyle}>Assign New Role</label>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
                        {assignableRoles.map(r => {
                            const m = ROLE_META[r];
                            const selected = role === r;
                            return (
                                <button
                                    key={r}
                                    onClick={() => setRole(r)}
                                    style={{
                                        flex: 1, minWidth: 100,
                                        padding: "10px 14px",
                                        borderRadius: 10,
                                        border: `2px solid ${selected ? m.color : C.border}`,
                                        background: selected ? m.bg : C.surface,
                                        color: selected ? m.color : C.muted,
                                        fontWeight: 700, fontSize: 13,
                                        cursor: "pointer",
                                        display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                                        transition: "all 0.15s",
                                    }}
                                >
                                    <span style={{ fontSize: 20 }}>{m.icon}</span>
                                    {m.label}
                                    {selected && <FiCheck style={{ fontSize: 12 }} />}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
                    <Btn onClick={handleSave} disabled={saving || role === member.role}>
                        {saving ? "Saving…" : "Update Role"}
                    </Btn>
                </div>
            </div>
        </Modal>
    );
}

// ── Reset Password Modal ───────────────────────────────────
function ResetPasswordModal({ member, toast, onClose, onSaved }) {
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (password.length < 6) { toast("Password must be at least 6 characters.", "error"); return; }
        setSaving(true);
        try {
            await aPut(`/users/staff/${member._id}/reset-password`, { password });
            toast(`Password reset for ${member.name} ✓`);
            onSaved();
        } catch (e) { toast(e.message, "error"); }
        finally { setSaving(false); }
    };

    return (
        <Modal title={`Reset Password — ${member.name}`} onClose={onClose}>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>
                    Set a new password for <strong>{member.name}</strong> ({member.email}). They should change it after logging in.
                </p>
                <div>
                    <label style={labelStyle}>New Password</label>
                    <div style={iconInputWrap}>
                        <FiLock style={iconStyle} />
                        <input
                            style={inputStyle}
                            type={show ? "text" : "password"}
                            placeholder="Min 6 characters"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                        />
                        <button
                            onClick={() => setShow(s => !s)}
                            style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 13 }}
                        >
                            {show ? <FiX /> : <FiKey />}
                        </button>
                    </div>
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
                    <Btn onClick={handleSave} disabled={saving || password.length < 6}>
                        {saving ? "Saving…" : <><FiKey /> Reset Password</>}
                    </Btn>
                </div>
            </div>
        </Modal>
    );
}

// ── Shared input styles ────────────────────────────────────
const labelStyle = {
    display: "block", fontSize: 12, fontWeight: 600,
    color: "#64748b", marginBottom: 6,
};
const iconInputWrap = {
    position: "relative", display: "flex", alignItems: "center",
};
const iconStyle = {
    position: "absolute", left: 10, fontSize: 13, color: "#94a3b8", pointerEvents: "none",
};
const inputStyle = {
    width: "100%", padding: "9px 12px 9px 30px",
    border: "1px solid rgba(99,102,241,0.15)", borderRadius: 8,
    background: "#f8f9fc", color: "#0f172a", fontSize: 13,
    outline: "none", boxSizing: "border-box",
};